import React from 'react';
import { Card, CardContent, CardMedia, Box, Typography, Chip, Stack, Divider, Button, Grid } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SdStorageOutlinedIcon from '@mui/icons-material/SdStorageOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import { formattedDate } from '../Utils/Functions';

function humanFileSize(bytes) {
  if (typeof bytes !== 'number') return '-';
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes.toFixed(1) + ' ' + units[u];
}

const typeIcon = (mimeType) => {
  if (!mimeType) return <InsertDriveFileOutlinedIcon fontSize="large" color="action" />;
  if (mimeType.startsWith('image/')) return <ImageOutlinedIcon fontSize="large" color="action" />;
  if (mimeType === 'application/pdf') return <PictureAsPdfOutlinedIcon fontSize="large" color="action" />;
  if (mimeType.includes('word') || mimeType.includes('msword') || mimeType.includes('officedocument')) return <DescriptionOutlinedIcon fontSize="large" color="action" />;
  return <InsertDriveFileOutlinedIcon fontSize="large" color="action" />;
};

/*
Props:
- certificate: {
    name: string,
    type: string, // MIME type
    sizeBytes?: number,
    createdAt?: string|Date,
    updatedAt?: string|Date,
    status?: 'active'|'expired'|'revoked'|string,
    fileUrl?: string, // direct URL to file
    thumbnailUrl?: string, // optional pre-generated image thumbnail URL
  }
- onClick?: () => void
*/
export default function CertificateCard({ certificate = {}, onClick }) {
  const {
    name,
    type,
    sizeBytes,
    createdAt,
    updatedAt,
    status,
    fileUrl,
    thumbnailUrl,
  } = certificate || {};

  const isImage = type && type.startsWith('image/');
  const previewSrc = thumbnailUrl || (isImage ? fileUrl : null);

  const statusColor = (() => {
    const s = (status || '').toString().toLowerCase();
    if (s === 'active' || s === 'valid') return 'success';
    if (s === 'expired') return 'warning';
    if (s === 'revoked' || s === 'invalid') return 'error';
    return 'default';
  })();

  const handleView = () => {
    if (onClick) return onClick();
    if (fileUrl) window.open(fileUrl, '_blank');
  };

  const handleDownload = () => {
    if (!fileUrl) return;
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = name || 'certificate';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ position: 'relative' }}>
        {previewSrc ? (
          <CardMedia component="img" height="170" image={previewSrc} alt={name || 'Certificate preview'} sx={{ objectFit: 'cover' }} />
        ) : (
          <Box sx={{ height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
            {typeIcon(type)}
          </Box>
        )}
        {status ? (
          <Chip
            size="small"
            label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
            color={statusColor}
            sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'background.paper' }}
          />
        ) : null}
      </Box>
      <CardContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap title={name}>
          {name || 'Untitled certificate'}
        </Typography>

        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item size={{xs:6}}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ArticleOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">Type:</Typography>
            </Stack>
          </Grid>
          <Grid item size={{xs:6}}>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{type || '-'}</Typography>
          </Grid>

          <Grid item size={{xs:6}}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SdStorageOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">Size:</Typography>
            </Stack>
          </Grid>
          <Grid item size={{xs:6}}>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{humanFileSize(sizeBytes)}</Typography>
          </Grid>

          <Grid item size={{xs:6}}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">Created:</Typography>
            </Stack>
          </Grid>
          <Grid item size={{xs:6}}>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{createdAt ? formattedDate(new Date(createdAt)) : '-'}</Typography>
          </Grid>

          <Grid item size={{xs:6}}>
            <Stack direction="row" spacing={1} alignItems="center">
              <UpdateOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">Modified:</Typography>
            </Stack>
          </Grid>
          <Grid item size={{xs:6}}>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{updatedAt ? formattedDate(new Date(updatedAt)) : '-'}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={handleView} disabled={!fileUrl && !onClick}>
            View
          </Button>
          <Button fullWidth variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={handleDownload} disabled={!fileUrl}>
            Download
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
